import { Col, List, Row, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import useBulkTicketChangeJobList from '../../../hooks/bulkJobs/useBulkTicketChangeJobList';
import { BulkTicketChangeJob } from '../../../services/tickets/tickets';
import { by } from '../../../utils/helpers';
import { JobStatusTypeMap } from '../../Pages/AdminPage/FiltersView/ExportFiltersView/ExportFiltersTable';
import { MAX_VISIBLE_JOB_FEEDBACK_COUNT } from '../constant';
import { fireBulkTicketChangeJobFinishEvent, setJobFeedbacksToStorage } from '../utils';
import JobProgress from './JobProgress';
import JobStatus from './JobStatus';

export default function JobFeedbackList({ jobs }: { jobs: BulkTicketChangeJob[] }) {
    const isJobFeedbackNeeded = !!jobs?.find(({ status }) =>
        [JobStatusTypeMap.INITIALIZING, JobStatusTypeMap.RUNNING].includes(status)
    );

    const previousFinishedJobCount = useRef(jobs.length);

    const { data } = useBulkTicketChangeJobList(jobs.map(({ id }) => id).join(','), {
        requestFlag: isJobFeedbackNeeded
    });

    const sortedData = useMemo(() => data?.sort(by<BulkTicketChangeJob>('startedAt')('descending')), [data]);

    useEffect(() => {
        sortedData && setJobFeedbacksToStorage(sortedData.sort(by<BulkTicketChangeJob>('startedAt')('descending')));
        const finishedJobCount = sortedData?.filter(({ status }) => status === JobStatusTypeMap.FINISHED)?.length ?? 0;
        if (
            (finishedJobCount > 0 && finishedJobCount >= previousFinishedJobCount.current) ||
            finishedJobCount === MAX_VISIBLE_JOB_FEEDBACK_COUNT
        ) {
            fireBulkTicketChangeJobFinishEvent();
            previousFinishedJobCount.current = finishedJobCount;
        }
    }, [sortedData]);

    return (
        <List<BulkTicketChangeJob>
            itemLayout="vertical"
            size="small"
            style={{ maxHeight: 500, overflowY: 'auto' }}
            dataSource={sortedData || jobs}
            renderItem={(item) => {
                return (
                    <List.Item key={item.id} style={{ paddingLeft: 0 }}>
                        <List.Item.Meta
                            style={{ marginBlockEnd: 0 }}
                            description={
                                <Row justify="space-between" align="middle" wrap={false}>
                                    <Col flex="auto">
                                        <Tooltip title={item.id}>
                                            <Typography.Text ellipsis>{item.id}</Typography.Text>
                                        </Tooltip>
                                    </Col>
                                    <Col offset={1} span={5}>
                                        <JobStatus value={item.status} />
                                    </Col>
                                    <Col offset={1} span={4}>
                                        <JobProgress value={item.progress} />
                                    </Col>
                                </Row>
                            }
                        />
                    </List.Item>
                );
            }}
        />
    );
}
